import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

import { BrandPortalModal, Button } from "../../../../components/design-system";
import { COPY } from "../copy";
import { copyParagraphs, renderCopy } from "../renderCopy";

const FORECAST_COPY = COPY.shared.forecast;

/**
 * Reference-only dialog behind the forecast explainer link. There is nothing to
 * decide here, so the footer carries a single dismissal rather than a
 * confirm/cancel pair — the same shape as the ad types dialog on the Overview.
 *
 * It exists because the first-year figures are the ones on the page that change
 * without the reader doing anything, and a metric that moves on its own reads
 * as unreliable until someone explains why it moves.
 */
export function FirstYearForecastModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <BrandPortalModal
      open={open}
      onClose={onClose}
      title={FORECAST_COPY.modal.title}
      footer={
        <div className="flex justify-end">
          <Button variant="primary" size="small" onClick={onClose}>
            {FORECAST_COPY.modal.confirm}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 pb-2 text-sm leading-5 text-[#333]">
        {copyParagraphs(FORECAST_COPY.modal.body).map((paragraph) => (
          <p key={paragraph}>{renderCopy(paragraph)}</p>
        ))}
      </div>
    </BrandPortalModal>
  );
}

const ForecastModalContext = createContext<(() => void) | null>(null);

/**
 * Owns the one forecast dialog for the whole page.
 *
 * It can't be owned by the thing that opens it. Most of the triggers live
 * inside tooltip panels, which unmount the moment the pointer leaves — a modal
 * rendered in there would be torn down on its way to being read. Hoisting it
 * here also means the several first-year figures scattered across the Overview
 * and the cohort view share a single dialog instance rather than one apiece.
 */
export function ForecastModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);

  return (
    <ForecastModalContext.Provider value={openModal}>
      {children}
      <FirstYearForecastModal open={open} onClose={() => setOpen(false)} />
    </ForecastModalContext.Provider>
  );
}

/**
 * Opens the forecast dialog. Returns null outside a provider so a component can
 * hide its link rather than render one that does nothing.
 */
export function useForecastModal(): (() => void) | null {
  return useContext(ForecastModalContext);
}

/**
 * The explainer link as it appears inside a tooltip panel: white on the dark
 * panel, and on its own line so it reads as an action rather than as the last
 * clause of the definition above it.
 */
export function ForecastTooltipLink() {
  const openModal = useForecastModal();
  if (!openModal) {
    return null;
  }

  return (
    <button
      type="button"
      className="text-fs-text-primary-inverse mt-2 block text-left text-[14px] leading-[20px] underline"
      onClick={openModal}
    >
      {FORECAST_COPY.link}
    </button>
  );
}

/**
 * Tooltip body for a partly-forecasted figure: its definition, then the link.
 *
 * A helper rather than a fragment at each call site because these tooltips are
 * spread across the month table and a chart legend, and the whole point of
 * adding the link in three places is that a reader meets the same affordance
 * each time.
 *
 * Any tooltip built with this has to be rendered with `interactive` on
 * `HeaderTooltip`, or the link can't be clicked.
 */
export function forecastTooltip(definition: string): ReactNode {
  return (
    <>
      {definition}
      <ForecastTooltipLink />
    </>
  );
}
